/**
 * T-085 — Install command generator (spec §5 Page 2, §8 src/views/commands.ts).
 *
 * Pure client-side module: once POST /api/links has issued a download URL,
 * this turns (artifact, URL, serving target, ABI) into the exact shell command
 * an operator pastes into a target VM. No network, no DOM — snapshot-tested
 * per spec §10 ("command generation snapshots for DEB/GCS ZIP/GitHub ZIP;
 * shell quoting tests").
 *
 * Command shapes (spec §5 Page 2; URL is always POSIX shell-quoted):
 *  - DEB: `curl -fL 'URL' -o /tmp/tnd.deb && sudo dpkg -i /tmp/tnd.deb && tnd-setup --all`
 *  - ZIP: `curl -fL 'URL' -o /tmp/tnd.zip && unzip -q … -d /tmp/tnd-dist
 *    && sudo dpkg -i /tmp/tnd-dist/tnd-install-linux/tensor-native-driver_*.deb
 *    && tnd-setup --all`
 *    (the bundle zip nests its payload one level down, under
 *    `tnd-install-linux/` — confirmed against the real release artifact;
 *    `dpkg -i /tmp/tnd-dist/*.deb` silently finds nothing there. The glob is
 *    pinned to `tensor-native-driver_*.deb` — the underscore right after
 *    `driver` matches only the core package, not the dash-prefixed
 *    `tensor-native-driver-trt-plugin`/`-trtllm-accel`/`-vllm-accel`
 *    siblings the zip also ships. A bare `.deb` wildcard installs all of them
 *    in one `dpkg -i` invocation; `-trt-plugin` depends on `libnvinfer10`/
 *    `libnvinfer-plugin10`, which are NOT installable without NVIDIA's
 *    TensorRT apt repo — confirmed live, it fails and dpkg's overall exit
 *    code goes nonzero even though the core package configured successfully,
 *    which silently aborts the `&&`-chained `tnd-setup` that follows. The
 *    `-trtllm-accel`/`-vllm-accel` packages are empty transitional
 *    metapackages (their payload now ships inside the core deb) and are
 *    harmless either way, but there is no reason to install them explicitly.)
 *  - `gcloud storage cp` replaces `curl` only when a `gs://` URI is
 *    intentionally selected (authenticated-operator alternative; external
 *    users receive HTTPS links).
 *  - A `sudo apt-get install` prefix is prepended on a clean-box basis
 *    (spec/installation.md §2 "a clean box has neither pip, unzip, nor
 *    OpenMPI"): `unzip` for ZIP kinds, plus `build-essential` +
 *    `libopenmpi-dev` + `openmpi-bin` whenever `servingTarget` is `trtllm` or
 *    `all` — `tnd-setup`'s TensorRT-LLM leg (`tnd-trtllm-setup`) pip-builds
 *    a C-extension dependency (`llist`) that fails with `gcc: No such file
 *    or directory` without a compiler present, and separately dlopens
 *    `libmpi.so` at import time.
 *
 * Wheel/tarball kinds have no spec template; their flows are grounded in the
 * 05-inference-accel customer install path (`pip install` of the torch-free
 * scheduler wheel into vLLM's python3) and the `/opt/tnd` install root used
 * throughout the harness. They never emit `tnd-setup` (that tool configures
 * DEB-based serving).
 */

import {
  type Artifact,
  type ArtifactKind,
  isAllowlistedObjectPath,
  type PythonAbi,
  SERVING_TARGET_FLAGS,
  type ServingTarget,
} from '../shared/api';

/** Inputs to install-command generation (artifact subset actually consulted). */
export interface InstallCommandInput {
  /** Selected artifact; only kind/pythonAbi/objectPath/provider influence output. */
  artifact: Pick<Artifact, 'kind' | 'pythonAbi'>;
  /**
   * Fully-qualified download URL returned by POST /api/links (managed or
   * direct GCS V4), or an intentionally selected `gs://` URI. Relative paths
   * are rejected: a copy-pasted command runs on a different machine.
   */
  downloadUrl: string;
  /** Serving target forwarded to `tnd-setup` for DEB/ZIP installs. */
  servingTarget: ServingTarget;
  /** Effective Python ABI; must agree with the artifact's pinned ABI. */
  pythonAbi: PythonAbi;
}

/** Machine-readable rejection reasons for {@link CommandInputError}. */
export type CommandInputErrorCode = 'empty_url' | 'relative_url' | 'unsupported_url_scheme' | 'abi_mismatch';

/** Raised when command inputs cannot produce a runnable command. */
export class CommandInputError extends Error {
  readonly code: CommandInputErrorCode;

  constructor(code: CommandInputErrorCode, message: string) {
    super(message);
    this.name = 'CommandInputError';
    this.code = code;
  }
}

/** Artifact kinds installed as a single .deb (spec §5 DEB template). */
export const DEB_ARTIFACT_KINDS: readonly ArtifactKind[] = ['core_deb', 'trt_plugin_deb'];

/** Artifact kinds installed as a zip of .deb packages (spec §5 ZIP template). */
export const ZIP_ARTIFACT_KINDS: readonly ArtifactKind[] = ['frozen_zip', 'stable_zip'];

/** True when the kind's generated command ends in `tnd-setup <flag>`. */
export function kindUsesServingTarget(kind: ArtifactKind): boolean {
  return DEB_ARTIFACT_KINDS.includes(kind) || ZIP_ARTIFACT_KINDS.includes(kind);
}

/**
 * POSIX single-quote shell quoting: wraps `value` in single quotes, escaping
 * embedded quotes as `'\''`. Safe for URLs containing `&`, `%`, `?`, spaces,
 * and shell metacharacters; the result is always exactly one shell word.
 */
export function shellQuote(value: string): string {
  return `'${value.replaceAll("'", "'\\''")}'`;
}

/** Download step: `curl` for HTTPS links, `gcloud storage cp` for gs:// URIs. */
function downloadStep(url: string, destination: string): string {
  if (url.startsWith('gs://')) {
    return `gcloud storage cp ${shellQuote(url)} ${destination}`;
  }
  return `curl -fL ${shellQuote(url)} -o ${destination}`;
}

function assertUsableUrl(downloadUrl: string): void {
  if (downloadUrl.trim() === '') {
    throw new CommandInputError('empty_url', 'Download URL is empty; issue a link first');
  }
  if (downloadUrl.startsWith('gs://')) return;
  if (/^https?:\/\//i.test(downloadUrl)) return;
  if (downloadUrl.startsWith('/')) {
    throw new CommandInputError(
      'relative_url',
      'Download URL must be absolute (https://…); resolve it against the deployment origin first',
    );
  }
  throw new CommandInputError(
    'unsupported_url_scheme',
    `Unsupported download URL scheme in ${JSON.stringify(downloadUrl.slice(0, 32))}…; expected https:// or gs://`,
  );
}

function assertAbiMatches(input: InstallCommandInput): void {
  const pinned = input.artifact.pythonAbi;
  if (pinned !== null && pinned !== input.pythonAbi) {
    throw new CommandInputError(
      'abi_mismatch',
      `Artifact is built for ${pinned} but ${input.pythonAbi} was selected; re-select the matching artifact`,
    );
  }
}

/**
 * System packages to `apt-get install` before the download/dpkg steps, for a
 * genuinely clean box (spec/installation.md §2). Returns `null` when the
 * kind/servingTarget combination needs nothing.
 */
function systemPrereqStep(kind: ArtifactKind, servingTarget: ServingTarget): string | null {
  const packages: string[] = [];
  if (ZIP_ARTIFACT_KINDS.includes(kind)) {
    packages.push('unzip');
  }
  if (servingTarget === 'trtllm' || servingTarget === 'all') {
    packages.push('build-essential', 'libopenmpi-dev', 'openmpi-bin');
  }
  if (packages.length === 0) return null;
  return `sudo apt-get update -qq && sudo apt-get install -y -qq ${packages.join(' ')}`;
}

/**
 * Generate the install command for the selected artifact and issued URL.
 * Deterministic: identical inputs always produce byte-identical output
 * (snapshot-test contract, spec §10).
 *
 * @throws {CommandInputError} on empty/relative/unsupported URLs or ABI mismatch.
 */
export function generateInstallCommand(input: InstallCommandInput): string {
  assertUsableUrl(input.downloadUrl);
  assertAbiMatches(input);

  const url = input.downloadUrl;
  const setupFlag = SERVING_TARGET_FLAGS[input.servingTarget];

  switch (input.artifact.kind) {
    case 'core_deb':
    case 'trt_plugin_deb': {
      const prereq = systemPrereqStep(input.artifact.kind, input.servingTarget);
      const prefix = prereq !== null ? `${prereq} && ` : '';
      return `${prefix}${downloadStep(url, '/tmp/tnd.deb')} && sudo dpkg -i /tmp/tnd.deb && tnd-setup ${setupFlag}`;
    }
    case 'frozen_zip':
    case 'stable_zip': {
      const prereq = systemPrereqStep(input.artifact.kind, input.servingTarget);
      const prefix = prereq !== null ? `${prereq} && ` : '';
      return (
        `${prefix}${downloadStep(url, '/tmp/tnd.zip')} && unzip -q /tmp/tnd.zip -d /tmp/tnd-dist` +
        ` && sudo dpkg -i /tmp/tnd-dist/tnd-install-linux/tensor-native-driver_*.deb && tnd-setup ${setupFlag}`
      );
    }
    case 'scheduler_wheel':
      return `${downloadStep(url, '/tmp/tnd-scheduler.whl')} && python3 -m pip install /tmp/tnd-scheduler.whl`;
    case 'decoder_tarball':
      return (
        `${downloadStep(url, '/tmp/tnd-decoder.tar')} && sudo mkdir -p /opt/tnd` +
        ` && sudo tar -xf /tmp/tnd-decoder.tar -C /opt/tnd`
      );
  }
}

/* ------------------------------------------------------------------------ */
/* Manual GCS targets                                                        */
/* ------------------------------------------------------------------------ */

/** Outcome of interpreting whatever the operator pasted into the manual field. */
type ManualParse =
  | { ok: true; objectPath: string; gsUri: string | null }
  | { ok: false; message: string };

/**
 * Interpret a manual GCS target. Accepts the three forms an operator actually
 * has on hand, and normalizes all of them to a bucket-relative object path:
 *
 *  - `gs://<bucket>/<object path>` — from `gcloud storage ls`. The original URI
 *    is kept so the authenticated `gcloud storage cp` flow stays available.
 *  - `https://storage.googleapis.com/<bucket>/<object path>` and
 *    `https://<bucket>.storage.googleapis.com/<object path>` — from the Cloud
 *    console. Any query string (e.g. an existing signature) is dropped: this
 *    field names an object, it does not carry a capability.
 *  - a bare `releases/…` / `debs/…` object path.
 *
 * The path must satisfy the same allowlist the server enforces, so an
 * unacceptable path is rejected here instead of costing a 403 round trip.
 */
export function parseManualObjectPath(raw: string): ManualParse {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: false, message: 'Enter a GCS object path.' };

  let objectPath = trimmed;
  let gsUri: string | null = null;

  if (/^gs:\/\//i.test(trimmed)) {
    const rest = trimmed.slice('gs://'.length);
    const slash = rest.indexOf('/');
    if (slash === -1 || slash === rest.length - 1) {
      return { ok: false, message: 'gs:// URI must include a bucket and an object path.' };
    }
    objectPath = rest.slice(slash + 1);
    gsUri = trimmed;
  } else if (/^https?:\/\//i.test(trimmed)) {
    let url: URL;
    try {
      url = new URL(trimmed);
    } catch {
      return { ok: false, message: 'Not a valid URL.' };
    }
    const path = url.pathname.replace(/^\/+/, '');
    if (url.hostname === 'storage.googleapis.com') {
      // Path-style: /<bucket>/<object path>
      const slash = path.indexOf('/');
      if (slash === -1 || slash === path.length - 1) {
        return { ok: false, message: 'URL must include a bucket and an object path.' };
      }
      objectPath = path.slice(slash + 1);
    } else if (url.hostname.endsWith('.storage.googleapis.com')) {
      // Virtual-hosted style: <bucket>.storage.googleapis.com/<object path>
      objectPath = path;
    } else {
      return {
        ok: false,
        message: 'Only gs:// URIs and storage.googleapis.com URLs are recognized.',
      };
    }
    objectPath = decodeURIComponent(objectPath);
  }

  if (!isAllowlistedObjectPath(objectPath)) {
    return {
      ok: false,
      message:
        `"${objectPath}" is outside the allowed namespaces. Expected ` +
        'releases/inference-engine/v/<version>/py3.10|py3.12/<file> or ' +
        'debs/run-<id>/py3.10|py3.12/<file>.deb.',
    };
  }
  return { ok: true, objectPath, gsUri };
}

/**
 * Best-effort artifact kind for a manual object, by file extension only.
 *
 * Deliberately weaker than the worker's `classifyArtifactName`: that function
 * also gates on the exact release naming convention, and the whole point of
 * manual entry is to serve objects that convention does not cover. This only
 * seeds the dropdown — the operator has the final say, and the kind decides
 * nothing more than which install template is emitted.
 */
export function inferArtifactKind(objectPath: string): ArtifactKind {
  const name = objectPath.slice(objectPath.lastIndexOf('/') + 1).toLowerCase();
  if (name.endsWith('.whl')) return 'scheduler_wheel';
  if (name.endsWith('.zip')) return 'frozen_zip';
  if (name.endsWith('.tar.gz') || name.endsWith('.tgz') || name.endsWith('.tar')) {
    return 'decoder_tarball';
  }
  if (name.includes('trt-plugin')) return 'trt_plugin_deb';
  return 'core_deb';
}

/** Python ABI encoded in a GCS path (`…/py3.10/…`) or a wheel tag (`-cp310-`). */
export function inferPythonAbi(objectPath: string): PythonAbi | null {
  const match = /(?:py3\.|-cp3)(10|12)/.exec(objectPath);
  if (match === null) return null;
  return `py3.${match[1] as '10' | '12'}`;
}
